import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api, { TOKEN_KEY } from "../lib/api";

const AuthContext = createContext(null);

const tokenAgeMs = (token) => { try { return Date.now() - JSON.parse(atob(token.split(".")[1])).iat * 1000; } catch { return 0; } };
export const keepAlive = async (force = false) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token || !navigator.onLine) return false;
  if (!force && tokenAgeMs(token) < 24 * 3600 * 1000) return false;
  try {
    const r = await api.post("/auth/refresh");
    if (r.data?.token) localStorage.setItem(TOKEN_KEY, r.data.token);
    return true;
  } catch { return false; }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(!!localStorage.getItem(TOKEN_KEY));

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    api
      .get("/auth/me")
      .then((r) => { setUser(r.data); keepAlive(); })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  // Sliding session: swap a token older than a day for a fresh one (on open, on reconnect, every 6h while open)
  useEffect(() => {
    const on = () => keepAlive();
    window.addEventListener("online", on);
    const t = setInterval(keepAlive, 6 * 3600 * 1000);
    return () => { window.removeEventListener("online", on); clearInterval(t); };
  }, []);

  const finish = (data) => {
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    return data.user;
  };

  const register = useCallback(async (email, password, name) => {
    const r = await api.post("/auth/register", { email, password, name });
    return finish(r.data);
  }, []);

  const login = useCallback(async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    return finish(r.data);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  }, []);

  const value = useMemo(() => ({ user, loading, register, login, logout }), [user, loading, register, login, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
